export interface AuthClientAdapter {
  $fetch: <T>(path: string, init?: RequestInit) => Promise<{ data?: T; error?: unknown }>;
  signInSocial: (options: SocialSignInOptions) => Promise<unknown>;
}

interface SocialSignInOptions {
  callbackURL?: string;
  disableRedirect?: boolean;
  errorCallbackURL?: string;
  idToken?: {
    token: string;
    nonce?: string;
    accessToken?: string;
    refreshToken?: string;
    expiresAt?: number;
  } | null;
  loginHint?: string;
  newUserCallbackURL?: string;
  provider: 'twitter';
  requestSignUp?: boolean;
  scopes?: string[];
}

const isFetchFn = (value: unknown): value is AuthClientAdapter['$fetch'] => typeof value === 'function';
const isSocialSignInFn = (value: unknown): value is AuthClientAdapter['signInSocial'] => typeof value === 'function';
const isIndexable = (value: unknown): value is { [key: string]: unknown } =>
  Boolean(value) && (typeof value === 'object' || typeof value === 'function');

export function createAuthClientAdapter(candidate: unknown, label = 'auth client'): AuthClientAdapter {
  if (!isIndexable(candidate)) {
    throw new TypeError(`${label} unavailable`);
  }

  // biome-ignore lint/complexity/useLiteralKeys: TS requires bracket access for index-signature shaped values.
  const fetchCandidate = candidate['$fetch'];
  if (!isFetchFn(fetchCandidate)) {
    throw new TypeError(`${label} fetch helper missing`);
  }

  // biome-ignore lint/complexity/useLiteralKeys: TS requires bracket access for index-signature shaped values.
  const signInCandidate = candidate['signIn'];
  if (!isIndexable(signInCandidate)) {
    throw new TypeError(`${label} sign-in helpers missing`);
  }

  // biome-ignore lint/complexity/useLiteralKeys: TS requires bracket access for index-signature shaped values.
  const socialCandidate = signInCandidate['social'];
  if (!isSocialSignInFn(socialCandidate)) {
    throw new TypeError(`${label} social sign-in helper missing`);
  }

  const boundFetch = fetchCandidate.bind(candidate);
  const fetchAdapter: AuthClientAdapter['$fetch'] = <T>(path: string, init?: RequestInit) => boundFetch<T>(path, init);
  const signInSocialAdapter: AuthClientAdapter['signInSocial'] = (options) =>
    socialCandidate.call(signInCandidate, options);

  return {
    $fetch: fetchAdapter,
    signInSocial: signInSocialAdapter,
  };
}
